import type { FileMetadata, Product, StreamProduct } from '@igus/iglidur-free-form-hub-interfaces';
import { ProductTile } from '@hub/common/ProductTile/ProductTile';
import { useTranslation } from 'next-i18next';
import { MaterialColorIcon } from '@hub/free-form-hub/free-form-hub-result/ui/MaterialColorIcon';
import { PriceInfo } from '@hub/free-form-hub/free-form-hub-result/ui/PriceInfo';
import { MaterialTeaser } from '@hub/free-form-hub/free-form-hub-result/ui/MaterialTeaser';
import { DeliveryInfo } from '@hub/free-form-hub/free-form-hub-result/ui/DeliveryInfo';
import { Button } from '@hub/common/Button/Button';
import { useAddToShoppingCart } from '@hub/free-form-hub/free-form-hub-details/BuyBox/useAddToShoppingCart';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useEffect } from 'react';
import { BuyBoxModals } from '@hub/common/BuyBox/modals/BuyBoxModals';
import { BoreholeAndChannelLimitationsModal } from '@hub/free-form-hub/common/borehole-limitations/BoreholeAndChannelLimitationsModal';

type TileProps = {
  product: StreamProduct;
  fileId: string;
  quantity: number;
  onNavigateToMaterialDetails: (productId: string) => void;
  metaData: FileMetadata;
  modelThumbnail?: string;
  showToast: (product: Product) => void;
};
const Tile = ({
  product,
  fileId,
  quantity,
  metaData,
  modelThumbnail,
  onNavigateToMaterialDetails,
  showToast,
}: TileProps) => {
  const { t } = useTranslation(['free-form-hub/freeFormHub', 'common']);
  const session = useSessionStore(state => state.session);
  const {
    addToShoppingCart,
    setActiveModal,
    activeModal,
    loading,
    boreholeAndChannelCheck,
    boreholeLimitationsModalVisible,
    closeBoreholeLimitationsModal,
    addToShoppingCartAfterBoreholeAcknowledgement,
    designerUrl,
  } = useAddToShoppingCart({
    session,
    quantity,
    uploadId: fileId,
    origin: product?.origin,
    materialName: product?.material.name,
    materialId: product?.material.id,
    metaData,
    modelThumbnail,
    procedure: product?.material.procedure,
  });

  const productImage = ({ material }: StreamProduct) => <MaterialColorIcon colorHex={material.colorHex} />;
  const productId = `${product.material.name}-${product.origin}-${product.material.procedure}-${fileId}`;

  useEffect(() => {
    if (activeModal === BuyBoxModals.ADD_TO_SHOPPING_CART && product.status === 'ready') {
      showToast(product);
      setActiveModal(null);
    }
  }, [activeModal]);

  const productInfo = (
    <div className="w-full flex flex-col">
      <MaterialTeaser material={product.material} />
      <div className="font-medium mt-5 text-lg">
        <PriceInfo
          uploadId={fileId}
          product={product}
          quantity={quantity}
          metaData={metaData}
          modelThumbnail={modelThumbnail}
          showToast={showToast}
        />
      </div>
      <div className="w-full flex flex-row mb-6">
        <span className="font-bold">Delivery time:</span>
        <div className="ml-4 flex flex-1">
          <DeliveryInfo delivery={product.delivery} isLoading={product.status === 'pending'} />
        </div>
      </div>
    </div>
  );

  const ctaButton = (
    <>
      <Button
        onClick={() => onNavigateToMaterialDetails(productId)}
        text={t(`CTA.GOTO_PRODUCT_PAGE`, { ns: 'free-form-hub/freeFormHub' })}
        className="w-full"
      />
      {product.status === 'ready' && (
        <Button
          disabled={loading}
          text={t(`${product.price.unit > 0 ? 'BUY_BOX.ADD_TO_CART' : 'BUY_BOX.REQUEST_QUOTE'}`, {
            ns: 'free-form-hub/freeFormHubDetails',
          })}
          className="w-full"
          onClick={() => addToShoppingCart()}
          loading={loading}
        />
      )}
    </>
  );

  return (
    <>
      <ProductTile productInfo={productInfo} image={productImage(product)} ctaButton={ctaButton} />
      {boreholeAndChannelCheck && (
        <BoreholeAndChannelLimitationsModal
          visible={boreholeLimitationsModalVisible}
          procedure={product.material.procedure}
          limits={boreholeAndChannelCheck.limits}
          designerUrl={designerUrl}
          onClose={closeBoreholeLimitationsModal}
          onConfirm={addToShoppingCartAfterBoreholeAcknowledgement}
        />
      )}
    </>
  );
};

export type FreeFormHubProductTileProps = {
  productList: StreamProduct[];
  fileId: string;
  onNavigateToMaterialDetails: (productId: string) => void;
  quantity: number;
  metaData: FileMetadata;
  modelThumbnail?: string;
  showToast: (product: Product) => void;
};
export const FreeFormHubProductTile = ({
  productList,
  fileId,
  onNavigateToMaterialDetails,
  quantity,
  metaData,
  modelThumbnail,
  showToast,
}: FreeFormHubProductTileProps) => {
  return (
    <>
      {productList.map(product => (
        <Tile
          fileId={fileId}
          metaData={metaData}
          onNavigateToMaterialDetails={onNavigateToMaterialDetails}
          product={product}
          quantity={quantity}
          key={`${product.material.id}-${product.material.procedure}-${product.origin}`}
          modelThumbnail={modelThumbnail}
          showToast={showToast}
        />
      ))}
    </>
  );
};
